import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, X, Sparkles, AlertTriangle, Trash2 } from "lucide-react";
import { api, gbp2 } from "@/lib/api";
import "@/styles/promotions.css";

const THEMES = [
  { id: "christmas",   label: "Christmas",     preset: { primary: "#173A2A", accent: "#C5A059", button: "#173A2A", text: "#FAF9F6" } },
  { id: "valentines",  label: "Valentine's",   preset: { primary: "#7A1F2B", accent: "#E9C6C6", button: "#7A1F2B", text: "#FAF9F6" } },
  { id: "summer",      label: "Summer",        preset: { primary: "#8A6E30", accent: "#F4EDDE", button: "#8A6E30", text: "#FAF9F6" } },
  { id: "black_friday",label: "Black Friday",  preset: { primary: "#141414", accent: "#C5A059", button: "#141414", text: "#FAF9F6" } },
  { id: "new_year",    label: "New Year",      preset: { primary: "#1B2A44", accent: "#C5A059", button: "#1B2A44", text: "#FAF9F6" } },
  { id: "custom",      label: "Custom",        preset: { primary: "#141414", accent: "#C5A059", button: "#141414", text: "#FAF9F6" } },
];
const ANIMATIONS = [
  { id: "none",     label: "None" },
  { id: "shimmer",  label: "Elegant Shimmer" },
  { id: "glow",     label: "Soft Glow" },
  { id: "pulse",    label: "Pulse" },
  { id: "sparkle",  label: "Sparkle" },
  { id: "seasonal", label: "Seasonal (auto)" },
];
const STATUS_META = {
  active:   { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "ACTIVE" },
  upcoming: { cls: "bg-amber-50 text-amber-800 border-amber-200",       label: "UPCOMING" },
  expired:  { cls: "bg-stone-100 text-stone-500 border-stone-200",      label: "EXPIRED" },
  disabled: { cls: "bg-red-50 text-red-700 border-red-200",             label: "DISABLED" },
};

export default function AdminPromotions() {
  const [rows, setRows] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [pr, tr] = await Promise.all([
        api.get("/admin/promotions"),
        api.get("/treatments/all"),
      ]);
      setRows(pr.data.rows || []);
      setTreatments(tr.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load promotions");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => setEditing({ __new: true,
    name: "", theme: "christmas", description: "", discount_pct: 20,
    apply_to: "all", treatment_ids: [],
    start_at: "", end_at: "", enabled: true,
    colors: THEMES[0].preset, animation: "seasonal", badge_label: "" });

  return (
    <div className="space-y-6" data-testid="admin-promotions">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <p className="mono-label">Treatments</p>
          <h2 className="font-serif text-3xl text-stone-900">Offers &amp; Promotions</h2>
          <p className="text-xs text-stone-500 mt-1">Run seasonal campaigns without touching your normal treatment prices.</p>
        </div>
        <button data-testid="create-promotion" onClick={openCreate}
                className="mono-label text-[11px] bg-stone-900 text-white border border-stone-900 px-4 py-2.5 inline-flex items-center gap-2 hover:bg-stone-800">
          <Plus size={12} /> Create promotion
        </button>
      </div>

      <div className="border hairline bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-500 mono-label text-[10px]">
            <tr>
              <Th>Promotion</Th>
              <Th>Theme</Th>
              <Th className="text-right">Discount</Th>
              <Th>Treatments</Th>
              <Th>Start</Th>
              <Th>End</Th>
              <Th>Status</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-4 py-6 text-center text-stone-500">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-stone-500" data-testid="promotions-empty">
                No promotions yet. Create your first seasonal campaign.
              </td></tr>
            )}
            {rows.map((p) => (
              <tr key={p.id} className="border-t hairline hover:bg-stone-50 cursor-pointer"
                  onClick={() => setEditing({ ...p })}
                  data-testid={`promotion-row-${p.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium">{p.name}</div>
                  <div className="text-xs text-stone-500">{p.description || "—"}</div>
                </td>
                <td className="px-4 py-3 mono-label text-[10px]">{p.theme?.toUpperCase()}</td>
                <td className="px-4 py-3 text-right font-mono gold">{p.discount_pct}% OFF</td>
                <td className="px-4 py-3 text-xs">
                  {p.apply_to === "all" ? "All treatments" : `${p.treatment_ids?.length || 0} selected`}
                </td>
                <td className="px-4 py-3 text-xs">{fmtDate(p.start_at)}</td>
                <td className="px-4 py-3 text-xs">{fmtDate(p.end_at)}</td>
                <td className="px-4 py-3">
                  <span className={`mono-label text-[10px] border px-2 py-1 ${STATUS_META[p.status]?.cls || ""}`}>
                    {STATUS_META[p.status]?.label || p.status?.toUpperCase()}
                  </span>
                </td>
                <td className="px-4 py-3 text-right"><span className="text-stone-500 text-xs">Edit</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <PromotionEditor
          initial={editing}
          treatments={treatments}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-left px-4 py-3 ${className}`}>{children}</th>;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}
function toDateInput(iso) {
  if (!iso) return "";
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return ""; }
}

function PromotionEditor({ initial, treatments, onClose, onSaved }) {
  const [form, setForm] = useState({ ...initial, start_at: toDateInput(initial.start_at), end_at: toDateInput(initial.end_at) });
  const [saving, setSaving] = useState(false);
  const [overlapWarning, setOverlapWarning] = useState(null);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const themeMeta = THEMES.find((t) => t.id === form.theme) || THEMES[0];

  // Apply theme preset when theme changes (only if colors match a preset already — never overwrite manual choices).
  useEffect(() => {
    setForm((f) => {
      const preset = (THEMES.find((t) => t.id === f.theme) || THEMES[0]).preset;
      // Only auto-apply if user hasn't customised (colors is empty or matches previous preset)
      if (!f.colors || Object.keys(f.colors).length === 0) return { ...f, colors: preset };
      return f;
    });
    // eslint-disable-next-line
  }, [form.theme]);

  const save = async (force = false) => {
    setSaving(true);
    setOverlapWarning(null);
    try {
      const payload = {
        name: form.name?.trim(),
        theme: form.theme, description: form.description || "",
        discount_pct: Number(form.discount_pct),
        apply_to: form.apply_to,
        treatment_ids: form.apply_to === "selected" ? form.treatment_ids : [],
        start_at: form.start_at, end_at: form.end_at,
        enabled: !!form.enabled,
        colors: form.colors || themeMeta.preset,
        animation: form.animation,
        badge_label: form.badge_label || null,
      };
      if (!payload.name) { toast.error("Name is required"); setSaving(false); return; }
      const url = form.__new ? `/admin/promotions${force ? "?force=true" : ""}` : `/admin/promotions/${form.id}${force ? "?force=true" : ""}`;
      const method = form.__new ? "post" : "patch";
      await api[method](url, payload);
      toast.success(form.__new ? "Promotion created" : "Promotion updated");
      onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && typeof detail === "object" && detail.overlap_id) {
        setOverlapWarning(detail);
      } else {
        toast.error(typeof detail === "string" ? detail : "Save failed");
      }
    }
    setSaving(false);
  };

  const del = async () => {
    if (!confirm("Delete this promotion?")) return;
    try {
      await api.delete(`/admin/promotions/${form.id}`);
      toast.success("Deleted");
      onSaved();
    } catch (e) { toast.error("Delete failed"); }
  };

  const previewStyle = {
    "--promo-primary": form.colors?.primary || themeMeta.preset.primary,
    "--promo-accent":  form.colors?.accent  || themeMeta.preset.accent,
    "--promo-button":  form.colors?.button  || themeMeta.preset.button,
    "--promo-text":    form.colors?.text    || themeMeta.preset.text,
  };
  const previewAnim = form.animation === "seasonal"
    ? { christmas: "sparkle", valentines: "glow", summer: "shimmer", black_friday: "pulse", new_year: "shimmer", custom: "shimmer" }[form.theme] || "shimmer"
    : form.animation;

  const promoPrice = 250 * (1 - Number(form.discount_pct || 0) / 100);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="promotion-editor">
      <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto border hairline">
        <div className="flex items-center justify-between px-6 py-4 border-b hairline sticky top-0 bg-white z-10">
          <div>
            <p className="mono-label">{form.__new ? "New promotion" : "Edit promotion"}</p>
            <p className="text-xs text-stone-500 mt-1">Base treatment prices are never changed — this is a display + checkout overlay.</p>
          </div>
          <button data-testid="editor-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="px-6 py-5 space-y-5">
          {overlapWarning && (
            <div className="border border-amber-300 bg-amber-50 p-3 text-xs" data-testid="overlap-warning">
              <div className="flex items-start gap-2">
                <AlertTriangle size={14} className="text-amber-700 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900">Overlapping promotion detected</p>
                  <p className="text-amber-800 mt-1">Another promotion — <strong>{overlapWarning.overlap_name}</strong> — already covers one or more of these treatments during this window. Saving anyway will let both exist, and the earlier-starting one will apply.</p>
                  <div className="mt-2 flex gap-2">
                    <button data-testid="save-force" onClick={() => save(true)} className="mono-label text-[10px] border border-amber-800 bg-amber-800 text-white px-3 py-1.5">SAVE ANYWAY</button>
                    <button onClick={() => setOverlapWarning(null)} className="mono-label text-[10px] border hairline px-3 py-1.5">CANCEL</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Promotion name"><input data-testid="field-name" className="w-full border hairline p-3 text-sm" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Christmas 2026" /></Field>
            <Field label="Theme">
              <select data-testid="field-theme" className="w-full border hairline p-3 text-sm bg-white" value={form.theme} onChange={(e) => { update("theme", e.target.value); update("colors", (THEMES.find((t) => t.id === e.target.value) || THEMES[0]).preset); }}>
                {THEMES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Discount %"><input data-testid="field-discount" type="number" min="1" max="90" className="w-full border hairline p-3 text-sm" value={form.discount_pct} onChange={(e) => update("discount_pct", e.target.value)} /></Field>
            <Field label="Animation">
              <select data-testid="field-animation" className="w-full border hairline p-3 text-sm bg-white" value={form.animation} onChange={(e) => update("animation", e.target.value)}>
                {ANIMATIONS.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
              </select>
            </Field>
            <Field label="Start date"><input data-testid="field-start" type="date" className="w-full border hairline p-3 text-sm" value={form.start_at} onChange={(e) => update("start_at", e.target.value)} /></Field>
            <Field label="End date"><input data-testid="field-end" type="date" className="w-full border hairline p-3 text-sm" value={form.end_at} onChange={(e) => update("end_at", e.target.value)} /></Field>
            <Field label="Description (optional)" full><textarea className="w-full border hairline p-3 text-sm" rows={2} value={form.description} onChange={(e) => update("description", e.target.value)} /></Field>
            <Field label="Badge label (optional)" full><input className="w-full border hairline p-3 text-sm" placeholder={`e.g. CHRISTMAS OFFER — defaults to ${form.discount_pct}% OFF`} value={form.badge_label || ""} onChange={(e) => update("badge_label", e.target.value)} /></Field>
          </div>

          <div>
            <p className="mono-label text-stone-500 text-[10px] mb-2">APPLY TO</p>
            <div className="flex gap-2 mb-3">
              {["all", "selected"].map((v) => (
                <button key={v} data-testid={`apply-${v}`} onClick={() => update("apply_to", v)}
                        className={`mono-label text-[10px] px-3 py-2 border ${form.apply_to === v ? "bg-stone-900 text-white border-stone-900" : "hairline bg-white text-stone-600"}`}>
                  {v === "all" ? "ALL TREATMENTS" : "SELECTED"}
                </button>
              ))}
            </div>
            {form.apply_to === "selected" && (
              <div className="border hairline p-3 max-h-56 overflow-y-auto space-y-1" data-testid="treatment-picker">
                {treatments.map((t) => {
                  const checked = form.treatment_ids?.includes(t.id);
                  return (
                    <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" data-testid={`treatment-${t.id}`} checked={checked}
                             onChange={() => {
                               const set = new Set(form.treatment_ids || []);
                               checked ? set.delete(t.id) : set.add(t.id);
                               update("treatment_ids", Array.from(set));
                             }} />
                      <span>{t.name}</span>
                      <span className="ml-auto text-xs text-stone-500 font-mono">£{Number(t.price).toFixed(0)}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <p className="mono-label text-stone-500 text-[10px] mb-2">COLOURS</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {["primary", "accent", "button", "text"].map((k) => (
                <label key={k} className="text-xs">
                  <span className="mono-label text-[10px] text-stone-500">{k.toUpperCase()}</span>
                  <input data-testid={`color-${k}`} type="color" className="w-full h-10 border hairline mt-1 cursor-pointer bg-white"
                         value={form.colors?.[k] || themeMeta.preset[k]}
                         onChange={(e) => update("colors", { ...form.colors, [k]: e.target.value })} />
                  <span className="font-mono text-[10px] text-stone-500">{form.colors?.[k] || themeMeta.preset[k]}</span>
                </label>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="field-enabled" checked={!!form.enabled} onChange={(e) => update("enabled", e.target.checked)} />
            <span>Enabled (uncheck to manually disable)</span>
          </label>

          {/* ── Live preview ──────────────────────────────── */}
          <div className="border hairline bg-stone-50 p-5" data-testid="promotion-preview">
            <p className="mono-label text-stone-500 text-[10px] mb-3">CLIENT PREVIEW</p>
            <div className="promo-card bg-white p-5 border hairline" style={previewStyle}>
              <p className="font-serif text-xl">Anti-Wrinkle · 3 Areas</p>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-stone-500 line-through">£250</span>
                <span className="font-serif text-2xl">£{promoPrice.toFixed(0)}</span>
                <span className="promo-badge mono-label text-[10px] ml-auto">{form.badge_label || `${form.discount_pct}% OFF`}</span>
              </div>
              <button className={`promo-cta mt-4 mono-label text-[11px] px-6 py-3 border animate-${previewAnim}`} data-testid="preview-cta">
                ADD TO CART <Sparkles size={12} className="inline-block ml-1 opacity-80" />
              </button>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t hairline flex items-center gap-2 sticky bottom-0 bg-white z-10">
          {!form.__new && (
            <button data-testid="editor-delete" onClick={del} className="mono-label text-[11px] text-red-700 border border-red-200 px-4 py-2.5 hover:bg-red-50 inline-flex items-center gap-2">
              <Trash2 size={12} /> Delete
            </button>
          )}
          <div className="ml-auto flex gap-2">
            <button onClick={onClose} className="mono-label text-[11px] border hairline px-4 py-2.5">CANCEL</button>
            <button data-testid="editor-save" onClick={() => save(false)} disabled={saving} className="mono-label text-[11px] bg-stone-900 text-white border border-stone-900 px-5 py-2.5 disabled:opacity-50">
              {saving ? "SAVING…" : (form.__new ? "CREATE" : "SAVE")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`text-sm ${full ? "md:col-span-2" : ""}`}>
      <span className="mono-label text-[10px] text-stone-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
